/* Recording a take, and turning it into a note in the margin.
 *
 * WHY THIS IS A COMPOSABLE AND NOT A SCREEN.
 *
 * All of this used to live inside CapturePage — which meant the recorder
 * lived on a SEPARATE SCREEN from the recipe. Pressing Record while editing
 * pushed you off the sheet onto a page in a different visual language, with
 * its own ingredient list, its own add-step field and its own step numbering
 * (it printed `position`, the sheet printed `i + 1`, so the bar could say
 * "goes on step 2" about a step the page above called 1). That is the screen
 * you are on while your mother is talking and your hands are wet.
 *
 * The logic below is the hardened part and is lifted verbatim: the salvage
 * path, the "no note without audio" rule, the retry, the announcements. It is
 * the code that decides whether somebody's voice survives, so it is moved
 * rather than rewritten, and there is now ONE copy of it with two mount
 * points instead of two copies drifting apart.
 */
import { ref, computed, unref } from 'vue';
import { useRecorder } from './recorder.js';
import { putAudio } from './audio-store.js';
import { addMarginNote } from '../store.js';

/** Seconds as a cook reads them: 0:07, 1:11. One formatter, so the running
 *  clock and the spoken announcement can never disagree about a duration. */
export function formatClock(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * @param {object} opts
 * @param {import('vue').Ref|string|number} opts.recipeId  which recipe the note belongs to
 * @param {() => (string|number|'')} opts.stepId  called at SAVE time, not at start
 *        time — someone can write another step while the recording runs, and
 *        the note belongs where the screen says it is going now.
 * @param {() => string} [opts.whereLabel]  how to name that destination aloud
 */
export function useTake({ recipeId, stepId, whereLabel }) {
  const saving = ref(false);
  const pendingClip = ref(null);
  const saveError = ref('');

  /* Said once when recording starts and once when it stops, for anyone who
     cannot see the button turn red or watch the clock run. The clock itself is
     a `role="timer"` with live updates off — announcing it every second is
     noise, and it drowns out the save errors underneath, which are the
     messages someone actually has to act on. */
  const announcement = ref('');

  /* The bytes go to lib/audio-store.js and the note stores the durable
     reference it hands back — `idb:<key>` — rather than an object URL, which
     is only valid until this page is closed.
     If the write fails there is NO NOTE: a row pointing at audio that was
     never written is a promise the app cannot keep. The clip is held in
     memory instead, said out loud, and offered again. */
  async function keepClip(clip) {
    saving.value = true;
    try {
      const mediaUrl = await putAudio(clip.blob);
      addMarginNote({
        recipeId: unref(recipeId),
        stepId: clip.stepId,
        kind: 'voice',
        body: '',
        mediaUrl,
        durationSeconds: clip.durationSeconds,
        recordedAt: clip.recordedAt,
      });
      pendingClip.value = null;
      saveError.value = '';
    } catch (err) {
      if (err && err.code === 'empty') {
        // Nothing was captured, so there is nothing to hold on to or retry.
        pendingClip.value = null;
        saveError.value = 'Nothing came through the microphone, so there was nothing to save.';
      } else {
        pendingClip.value = clip;
        saveError.value =
          'That recording was not saved — there may be no room left on this device. It is still here until you leave this screen.';
      }
    } finally {
      saving.value = false;
    }
  }

  /** The one route from "there are bytes" to "there is a note". Pressed Stop,
   *  walked off the screen, took a phone call — all three land here. */
  async function keepTake(result) {
    if (!result || !result.blob) return;
    const where = (whereLabel && whereLabel()) || 'the recipe';
    announcement.value =
      `Recording stopped, ${formatClock(result.durationSeconds)}, pinned to ${where}.`;
    await keepClip({
      blob: result.blob,
      durationSeconds: result.durationSeconds,
      stepId: stepId() || '',
      recordedAt: new Date().toISOString(),
    });
  }

  /* A TAKE THAT ENDS WITHOUT ANYONE PRESSING STOP STILL HAS TO BE SAVED.
   *
   * Leaving mid-recording used to drop the audio on the floor: useRecorder's
   * unmount handler killed the microphone and never stopped the recorder, so
   * the chunks went with the component and nothing was written, said, or
   * logged. On an iPhone that is a swipe from the left edge while somebody is
   * still talking.
   *
   * This callback can run AFTER the component is unmounted — the store and the
   * audio store are module singletons and do not care — so it must not assume
   * the screen is still there. It does not. */
  const {
    recording, elapsedSeconds, supported, micRefused, error: recorderError, start, stop,
  } = useRecorder({
    onSalvage: (clip) => { void keepTake(clip); },
  });

  const clock = computed(() => formatClock(elapsedSeconds.value));

  const message = computed(() => {
    if (recorderError.value) return recorderError.value;
    if (!supported.value) return 'This browser cannot record audio here. Type the note instead.';
    return '';
  });

  async function toggle() {
    if (saving.value) return;
    if (recording.value) {
      const result = await stop();
      if (result) await keepTake(result);
      else announcement.value = 'Recording stopped.';
    } else {
      saveError.value = '';
      announcement.value = '';
      await start();
      if (recording.value) {
        announcement.value = 'Recording.';
      } else if (micRefused.value) {
        // Short, and not a word-for-word repeat of the line now on screen: the
        // message paragraph is not a live region, so this is the only thing
        // said aloud, and it should say what to do rather than restate it.
        announcement.value = 'Microphone not available. Try again, or turn it on in Settings.';
      }
    }
  }

  /* THE CLEAN WAY OFF THE SCREEN, for the page to call from its own lifecycle
   * hook — Ionic's `onIonViewWillLeave` fires while the page is still fully
   * alive, early enough that MediaRecorder's asynchronous final
   * `dataavailable` arrives and the write to IndexedDB gets to start.
   *
   * Deliberately not awaited by callers: no navigation hook can hold a page
   * transition open for a media event, and pretending otherwise would just
   * mean a guard that blocks the Back button and still loses the audio.
   * useRecorder's own unmount handler is the net under this. Whichever gets
   * there first wins; the recorder only ever hands a take over once. */
  function flush() {
    if (!recording.value) return;
    void (async () => {
      const result = await stop();
      if (result) await keepTake(result);
    })();
  }

  function retrySave() {
    if (pendingClip.value) keepClip(pendingClip.value);
  }

  return {
    recording, supported, micRefused, clock, message,
    saving, saveError, pendingClip, announcement,
    toggle, flush, retrySave, keepClip, keepTake,
  };
}
